/* eslint-disable no-param-reassign */
/* eslint-disable prefer-const */
/* eslint-disable camelcase */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  FormField2,
  IMultiSelectControlField2,
  ISelectOption,
  toFormFields2
} from 'src/lib';
import {
  ICombinationModel,
  IFilterObj
} from 'src/components/filters/models/filter-response.model';
import { getNextValues } from 'src/components/filters';
import {
  Header,
  ITableData,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';

import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import { detectIdenticalObjects } from '../utils/utils';
import { SKUPopupType } from '../enums/sku-popup-enum';
import { ISkuSelectedDetails } from '../types/sku-popup-model';
import { IDuplicateSkuTable } from '../types/duplicate-sku-model';
import {
  fetchMetadata,
  createNewSku,
  editSku,
  overwriteSku,
  createOverwriteSKU,
  getInnovationTableData,
  deleteSku,
  getDuplicateSkuPopupFilters,
  duplicateCurrSku,
  getSimulatedInnovationData,
  getDuplicateSkuTable,
  duplicateBulkSku,
  getSaveSimulatedInnovation,
  getResetSimulatedInnovation,
  fetchOverwriteMetadata
} from './innovation-slice-async-thunks';
import {
  InnovationErrorMessages,
  SummaryStripModuleObject
} from '../../constants/constant';

export interface IInnovationState {
  fetchingMetaData: boolean; // boolean variable for fetching metadata
  fetchingOverwriteData: boolean; // boolean variable for fetching overwrite data
  skuDetailsObj: FormField2[]; // for storing options for inputs fields on sku popup
  skuFields: any[]; // for storing all the relavant keys of sku object/row
  skuFieldsCombinations: ICombinationModel[]; // for storing combinations of fields for sku metadata
  selectedSkuDetails: ISkuSelectedDetails; // for storing selected input fields on sku popup
  defaultSelections: ISkuSelectedDetails; // for storing default options for input fields on sku popup, used for resetting input fields
  masterDefaultSelections: ISkuSelectedDetails; // for storing empty values for create sku popup
  skuRowObj: object; // for storing details of sku as formatted object for row for innovation table
  /* Flag for fetching the table metadata */
  fetchingTableData: boolean;
  /* Flag for fetching simulated table data */
  fetchingSimulatedData: boolean;
  isSimulated: boolean; // boolean flag to set is simulated
  savingSimulation: boolean; // boolean flag for saving simulation
  /* Variable to maintain the output table data */
  resultCols: Header[];
  /* Variable to maintain the input table data */
  inputCols: Header[];
  innovationTableMetaData: ITableData;
  isRowEdited: boolean; // boolean for checking edit
  postSkuDeletionInnovationGridData: any;
  skusInnovatedCount: number; // to maintain total innovated skus
  isTableValidState: boolean; // to maintain the state of innovation table about all rows are unique or not
  skusUniqueList: string[];
  isSkuPopupVisible: boolean;
  skuPopupType: string;
  currentEditedSku: object; // current sku object when user has clicked on edit icon
  activeSkuIndex: number; // sku index of clicked sku for edit operation
  activeSkuId: number; // sku id of clicked sku for edit operation
  isSkuCRUDLoading: boolean; // loading variable while sku is created/edited/deleted
  isOverwriteMetadataLoading: boolean; // loading variable while overwrite metadata is fetched
  topFiltersValue: {
    filters: FormField2[];
    initialValues: { [key: string]: { label: string; value: string }[] };
    combinations: { [key: string]: string | number }[];
  }; // filters for duplcate sku popup
  topFiltersDefaultValue: {
    filters: FormField2[];
    initialValues: { [key: string]: { label: string; value: string }[] };
    combinations: { [key: string]: string | number }[];
  }; // default filters for duplcate sku popup
  fetchingDuplicateSkuPopupFilters: boolean;
  duplicateSkuTableData: {
    existing_sku_data: ITableData;
    new_sku_data: ITableData;
  };
  duplicateSkuCountMappingHash: {
    new_skus_data: object;
    existing_skus_data: object;
  };
  fetchingDuplicateSkuTable: boolean;
  isDuplicateEnabled: boolean;
  isBulkDuplicateResetClicked: boolean;
  prevSkuName: string; // previous sku name to be stored for edit operation
  moduleValue: number; // module id of sku for edit operation
  isInnovationTableValid: boolean; // boolean flag to check if there are unique and complete sku records in table
  appliedFilters: IFilterObj[];
  temp: any;
  skuOverwriteFields: { fields: [{ [key: string]: any }] }; // for storing all the relavant keys of overwrite sku row for modal
  isMoSubmitDone: boolean;
  isMoSimulateDone: boolean;
  createCurrentOverwrittenObj: any;
  createCurrentOverwrittenObjBase: any; // used for resetting the values to default one
  lastSimulatedSnapshot: any | null;

}

// default selection object for create sku dialog
const defaultSkuSelections: ISkuSelectedDetails = {
  sku: '',
  ppg: [],
  objective: '',
  brand: [],
  variant: [],
  channel: [],
  retailer: [{ label: 'All', value: 'All' }],
  category: [],
  cell: [],
  packtype: [],
  flavour: [],
  pack: [],
  pack_size: '',
  pack_count: [],
  implement_after: [],
  list_price_per_unit: '',
  cogs_per_unit: '',
  distribution: '',
  trade_rate_perc: '',
  average_unit_price: '',
  additional_media: 0
};

// mapping object for duplicate sku dialog table sections
const sku_list_section_map = {
  'Existing SKU': 'existing_skus_data',
  'New SKU': 'new_skus_data'
};

const initialState: IInnovationState = {
  fetchingMetaData: false,
  fetchingOverwriteData: true,
  skuDetailsObj: [],
  skuFields: [],
  skuFieldsCombinations: [],
  selectedSkuDetails: defaultSkuSelections,
  defaultSelections: defaultSkuSelections,
  masterDefaultSelections: defaultSkuSelections,
  skuRowObj: {},
  fetchingTableData: false,
  fetchingSimulatedData: false,
  isSimulated: false,
  savingSimulation: false,
  resultCols: [],
  inputCols: [],
  innovationTableMetaData: {
    legend: {
      metadata: []
    },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: { enabled: false, label: '' },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: [],
    summary_strip: {
      metadata: [],
      data: {
        internal_dt: 0,
        external_dt: 0,
        renovation: SummaryStripModuleObject,
        pricing: SummaryStripModuleObject,
        delist: SummaryStripModuleObject,
        distribution: SummaryStripModuleObject,
        innovation_table: SummaryStripModuleObject
      }
    },
    summary_strip_data: {},
    sku_name_list: []
  },
  isRowEdited: false,
  postSkuDeletionInnovationGridData: {},
  skusInnovatedCount: 0,
  isTableValidState: false,
  skusUniqueList: [],
  isSkuPopupVisible: false,
  skuPopupType: '',
  currentEditedSku: {},
  activeSkuIndex: 0,
  activeSkuId: 0,
  isSkuCRUDLoading: false,
  isOverwriteMetadataLoading: false,
  topFiltersValue: {
    filters: [],
    combinations: [],
    initialValues: {}
  },
  topFiltersDefaultValue: {
    filters: [],
    combinations: [],
    initialValues: {}
  },
  fetchingDuplicateSkuPopupFilters: false,
  duplicateSkuTableData: {
    existing_sku_data: {
      row: [],
      footer: {
        metadata: {
          enabled: false,
          label: ''
        },
        row: []
      },
      header: [],
      legend: {
        metadata: []
      }
    },
    new_sku_data: {
      row: [],
      footer: {
        metadata: {
          enabled: false,
          label: ''
        },
        row: []
      },
      header: [],
      legend: {
        metadata: []
      }
    }
  },
  duplicateSkuCountMappingHash: {
    new_skus_data: {},
    existing_skus_data: {}
  },
  fetchingDuplicateSkuTable: false,
  isDuplicateEnabled: false,
  isBulkDuplicateResetClicked: true,
  prevSkuName: '',
  moduleValue: 0,
  isInnovationTableValid: false,
  appliedFilters: [],
  temp: 0,
  skuOverwriteFields: { fields: [{}] },
  isMoSubmitDone: false,
  isMoSimulateDone: false,
  createCurrentOverwrittenObj: [],
  createCurrentOverwrittenObjBase: [],
  lastSimulatedSnapshot: null
};

// handler for updating post CRUD operations state
function updateState(currState: IInnovationState, skuData, tableData) {
  // checking for identical rows
  const { tableArray, isTableValid } = detectIdenticalObjects(tableData, [
    ...currState.skuFields
  ]);

  // updating the table state with newly innovated sku
  currState.innovationTableMetaData.row = tableArray;

  // updating the isTableValidState
  currState.isTableValidState = isTableValid;

  // updating innovated skus count
  if (skuData.summary_strip.data.innovation_table) {
    currState.skusInnovatedCount = skuData.summary_strip.data.innovation_table
      .value
      ? skuData.summary_strip.data.innovation_table.value
      : 0;
  }

  // updating the simulation state
  currState.isSimulated = false;
}

const innovationSlice = createSlice({
  name: 'innovation',
  initialState,
  reducers: {
    // reducer for setting sku popup input name values
    setInputFieldValues(state, action) {
      const { field_value, field_name } = action.payload;
      // eslint-disable-next-line prefer-const
      let currSelectedSkuDetails = JSON.parse(
        JSON.stringify(state.selectedSkuDetails)
      );

      // setting specific input name value
      currSelectedSkuDetails[field_name] = field_value;

      // updating the state
      state.selectedSkuDetails = currSelectedSkuDetails;
    },
    // reducer to open sku popup and edit sku input name values
    editInputFieldValues(state, action) {
      let currSkuObj = action.payload.original;
      // storing the current edit active sku
      state.currentEditedSku = currSkuObj;
      state.activeSkuIndex = action.payload.index;
      state.activeSkuId = currSkuObj.pk_id;

      // setting previous sku name
      state.prevSkuName = currSkuObj.sku;

      // setting module id for current sku
      state.moduleValue = currSkuObj.module;

      const list = [
        ...state.skusUniqueList.filter((e) => e !== currSkuObj.sku)
      ];
      state.skusUniqueList = list;

      state.selectedSkuDetails = currSkuObj;
      state.defaultSelections = currSkuObj;
      state.skuPopupType = SKUPopupType.EDIT;
      state.isSkuPopupVisible = true;
    },

    // case for updating footer
    updateFooter(state) {
      // Getting table data state
      // eslint-disable-next-line prefer-const
      let clonedTableData = JSON.parse(
        JSON.stringify(state.innovationTableMetaData)
      );
      // getting the keys of output section columns
      const outputSectionKeys = clonedTableData.header
        .filter(
          (ele) => ele.parent.section === 'output' && ele.parent.is_visible
        )
        .map((outputItem) => outputItem.parent.parent_field);

      // getting the footer row
      let footerRowObj = clonedTableData.footer;
      // getting the current edited row
      const editedRowObj = JSON.parse(JSON.stringify(state.currentEditedSku));

      // update state only if footer row is not empty
      if (footerRowObj.row.length) {
        outputSectionKeys.forEach((element) => {
          // getting the subtracted value for each column
          const subtractedValue =
            footerRowObj.row[0].parent.data[element] - editedRowObj[element];
          // subtract the editedRowObj values from footerRowObj for the outputSectionKeys array (considering values to be >< 0.01)
          footerRowObj.row[0].parent.data[element] =
            Math.abs(subtractedValue) > 0.01 ? subtractedValue : 0;
        });

        // updating the footer of table with new values
        clonedTableData.footer = footerRowObj;
        state.innovationTableMetaData = clonedTableData;
      }
    },

    // case for making isRowEdited false after footer is updated
    toggleTableDataFetched(state) {
      // updating the table data state
      state.isRowEdited = false;
    },

    overwriteInnovation(state, action) {
      state.createCurrentOverwrittenObj = action.payload.original;
    },
    // case for updating overwrite input fields on change
    updateOverwriteInputFields(state, action) {
      const { name, value } = action.payload;

      let currSkuObj = { ...state.createCurrentOverwrittenObj };
      currSkuObj[name] = value;
      state.createCurrentOverwrittenObj = currSkuObj;
    },
    // case for resetting the overwrite input fields to default
    resetOverwriteInputFields(state) {
      state.createCurrentOverwrittenObj = state.createCurrentOverwrittenObjBase;
    },

    closeOverwritePopup(state, action) {
      state.isSkuPopupVisible = action.payload;
    },
    closeSkuPop(state, action) {
      state.isSkuPopupVisible = action.payload;
    },
    setskuPopupType(state, action) {
      state.skuPopupType = action.payload;
      // resetting the innovated sku selected inputs
      if (action.payload === SKUPopupType.CREATE) {
        state.selectedSkuDetails = state.masterDefaultSelections;
        state.defaultSelections = state.masterDefaultSelections;
      }
    },
    resetInputFieldValues(state) {
      // resetting the selected fields with default option
      const defaultOptions = JSON.parse(
        JSON.stringify(state.defaultSelections)
      );
      state.selectedSkuDetails = defaultOptions;
    },
    updateFilterFieldOptions(
      state,
      action: PayloadAction<{
        [filterName: string]: ISelectOption[];
      }>
    ) {
      const remainingFilterValues = action.payload;
      state.topFiltersValue.filters = state.topFiltersValue.filters.map(
        (filter) => {
          if (remainingFilterValues[filter.name]) {
            return { ...filter, values: remainingFilterValues[filter.name] };
          }
          return filter;
        }
      );
    },
    setDuplicateSkuCount(state, action) {
      const { checked, skuName, no_of_duplicates, section } = action.payload;
      // cloning the duplicateSkuCountMappingHash
      let clonedSkuDuplicateObj = JSON.parse(
        JSON.stringify(state.duplicateSkuCountMappingHash)
      );

      // deleting the sku if it is unchecked
      if (!checked) {
        delete clonedSkuDuplicateObj[sku_list_section_map[section]][skuName];
      } else {
        clonedSkuDuplicateObj[sku_list_section_map[section]][skuName] =
          no_of_duplicates;
      }

      if (
        _.isEmpty(clonedSkuDuplicateObj.new_skus_data) &&
        _.isEmpty(clonedSkuDuplicateObj.existing_skus_data)
      ) {
        // setting isDuplicateEnabled to false
        state.isDuplicateEnabled = false;
        // setting isBulkDuplicateResetClicked to true in case of empty selection
        state.isBulkDuplicateResetClicked = true;
      } else {
        // setting isDuplicateEnabled to true
        state.isDuplicateEnabled = true;
        // setting isBulkDuplicateResetClicked to false in case of any selection
        state.isBulkDuplicateResetClicked = false;
      }

      // updating the state of duplicateSkuCountMappingHash
      state.duplicateSkuCountMappingHash = clonedSkuDuplicateObj;
    },
    resetDuplicateSku(state) {
      // resetting the selected skus for duplication
      state.duplicateSkuCountMappingHash = {
        new_skus_data: {},
        existing_skus_data: {}
      };

      // setting isDuplicateEnabled to false
      state.isDuplicateEnabled = false;

      // setting isBulkDuplicateResetClicked to true
      state.isBulkDuplicateResetClicked = true;
    },

    // handler for resetting duplicate filters and table
    resetDuplicateDialog(state) {
      // resetting duplicate dialog filters
      state.topFiltersValue = {
        filters: [],
        combinations: [],
        initialValues: {}
      };

      // resetting duplicate dialog sku table
      state.duplicateSkuTableData = {
        existing_sku_data: {
          row: [],
          footer: {
            metadata: {
              enabled: false,
              label: ''
            },
            row: []
          },
          header: [],
          legend: {
            metadata: []
          }
        },
        new_sku_data: {
          row: [],
          footer: {
            metadata: {
              enabled: false,
              label: ''
            },
            row: []
          },
          header: [],
          legend: {
            metadata: []
          }
        }
      };
    },
    //  handler for enabling/disabling create sku form fields as per toggle value
    toggleDisableStateForSkuFormFields(
      state,
      action: PayloadAction<{
        isExistingPpgSelected: boolean;
      }>
    ) {
      const { isExistingPpgSelected } = action.payload;
      state.skuDetailsObj
        .filter((e) => e.is_auto_fill)
        .forEach((ele) => {
          ele.is_active = isExistingPpgSelected;
        });
    },

    setSkuFormFieldOptions(
      state,
      action: PayloadAction<
        ReturnType<typeof getNextValues>['fieldNameToOptionsMap']
      >
    ) {
      const fieldNameToOptionsMap = action.payload;
      state.skuDetailsObj = state.skuDetailsObj.map((filter) => {
        if (fieldNameToOptionsMap[filter.name]) {
          return { ...filter, values: fieldNameToOptionsMap[filter.name] };
        }
        return filter;
      });
    },
    // case for updating isMoSubmitDone flag and isMoSimulateDone flag after imo submit simulation is done
    updateOverwrittenSimulateFlag(state) {
      // updating the isMOSubmit flag to false
      state.isMoSubmitDone = false;
      // updating the MO simulation flag to true
      state.isMoSimulateDone = true;
    },

    updateOvewrwrittenTableRow(state, action) {
      // getting overwritten sku row
      const skuData = action.payload;

      // Getting table data state
      // eslint-disable-next-line prefer-const
      let tableData = JSON.parse(
        JSON.stringify(state.innovationTableMetaData.row)
      );

      tableData = skuData;

      state.innovationTableMetaData.row = tableData;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchMetadata.pending, (state) => {
        state.fetchingMetaData = true;
      })
      .addCase(fetchMetadata.rejected, (state) => {
        state.fetchingMetaData = false;
      })
      .addCase(fetchMetadata.fulfilled, (state, action) => {
        const { fields, combinations, sku_name_list } = action.payload;
        const metaData: FormField2[] = fields;
        // assigning metadata
        state.skuDetailsObj = metaData;

        // assigning combinations
        state.skuFieldsCombinations = combinations;

        // assigning unique sku name list
        const uniqSkuNames = sku_name_list;
        state.skusUniqueList = uniqSkuNames;

        if (state.skuPopupType === SKUPopupType.EDIT) {
          const list = [...uniqSkuNames.filter((e) => e !== state.prevSkuName)];
          state.skusUniqueList = list;
        }
        if (state.skuPopupType === SKUPopupType.OVERWRITE) {
          const list = [...uniqSkuNames.filter((e) => e !== state.prevSkuName)];
          state.skusUniqueList = list;
        }

        // filtering sku uniq list by current active sku
        // const list = [
        //   ...state.skusUniqueList.filter((e) => e !== currSkuObj.sku)
        // ];
        // defaultSelections
        // setting sku fields which are only mandatory and not sku as identical check doesn't cound sku name
        state.skuFields = metaData
          .filter((e) => e.name !== 'sku' && e.is_mandatory)
          .map((ele) => ele.name);

        // assigning default values to input fields for create sku
        const inputFieldValues = metaData.reduce((result, obj) => {
          if (obj.control_type.includes('DROPDOWN') || obj.name === 'ppg') {
            // eslint-disable-next-line no-param-reassign
            result[obj.name] = [];
          } else {
            // eslint-disable-next-line no-param-reassign
            result[obj.name] = obj.control_type !== 'SLIDER-INPUT' ? '' : 0;
          }

          return result;
        }, {});

        // updating selected items state
        state.selectedSkuDetails = inputFieldValues as ISkuSelectedDetails;
        // setting default items state, it will be used for reset
        state.defaultSelections = inputFieldValues as ISkuSelectedDetails;

        state.fetchingMetaData = false;
      })
      .addCase(createNewSku.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(createNewSku.rejected, (state) => {
        state.isSkuCRUDLoading = false;
      })
      .addCase(createNewSku.fulfilled, (state, action) => {
        // getting innovated sku object
        const skuData = action.payload;
        const { newskus } = action.meta.arg;

        // performing create operations only the we recieve sku row
        if (skuData.row.length > 0) {
          // udpating the table state
          // eslint-disable-next-line prefer-const
          let tableData = JSON.parse(
            JSON.stringify(state.innovationTableMetaData.row)
          );

          tableData.push(skuData.row[0]);

          updateState(state, skuData, tableData);
        }

        // updating the loading state
        state.isSkuCRUDLoading = false;
      })
      .addCase(duplicateCurrSku.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(duplicateCurrSku.rejected, (state) => {
        state.isSkuCRUDLoading = false;
      })
      .addCase(duplicateCurrSku.fulfilled, (state, action) => {
        // getting innovated sku object
        const skuData = action.payload;

        // udpating the table state
        // eslint-disable-next-line prefer-const
        let tableData = JSON.parse(
          JSON.stringify(state.innovationTableMetaData.row)
        );
        // pushing the duplicated row in the tableData
        tableData.push(skuData.row[0]);

        updateState(state, skuData, tableData);

        // updating the loading state
        state.isSkuCRUDLoading = false;
      })
      .addCase(duplicateBulkSku.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(duplicateBulkSku.rejected, (state) => {
        state.isSkuCRUDLoading = false;
      })
      .addCase(duplicateBulkSku.fulfilled, (state, action) => {
        // getting duplicated sku list
        const skuData = action.payload;

        // eslint-disable-next-line prefer-const
        let tableData = JSON.parse(
          JSON.stringify(state.innovationTableMetaData.row)
        );

        // resetting the selected skus for duplication
        state.duplicateSkuCountMappingHash = {
          new_skus_data: {},
          existing_skus_data: {}
        };

        // setting isDuplicateEnabled to false
        state.isDuplicateEnabled = false;

        // adding duplicated skus to the table
        skuData.row.forEach((element) => {
          tableData.push(element);
        });

        updateState(state, skuData, tableData);

        // updating the loading state
        state.isSkuCRUDLoading = false;
      })
      .addCase(editSku.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(editSku.rejected, (state) => {
        state.isSkuCRUDLoading = false;
      })
      .addCase(editSku.fulfilled, (state, action) => {
        // getting modified sku object
        const skuData = action.payload;
        // getting current/non-edited sku name for deletion purpose
        const { skuName } = action.meta.arg;

        // Getting table data state
        // eslint-disable-next-line prefer-const
        let tableData = JSON.parse(
          JSON.stringify(state.innovationTableMetaData.row)
        );

        // performing create operations only the we recieve sku row
        if (skuData.row.length > 0) {
          // updating the edited row in table
          tableData[state.activeSkuIndex] = {
            parent: { data: skuData.row[0].parent.data }
          };
        }
        // deleting the row if we get empty data on edited sku
        else {
          // removing the sku from table data by sku name
          tableData = tableData.filter(
            (ele) => ele.parent.data.sku !== skuName
          );
        }

        updateState(state, skuData, tableData);

        // updating loading state
        state.isSkuCRUDLoading = false;

        state.isRowEdited = true;
      })

      .addCase(fetchOverwriteMetadata.pending, (state) => {
        state.fetchingOverwriteData = true;
      })
      .addCase(fetchOverwriteMetadata.rejected, (state) => {
        state.fetchingOverwriteData = false;
      })
      .addCase(fetchOverwriteMetadata.fulfilled, (state, action) => {
        state.fetchingOverwriteData = false;
        const overwriteFieldsMetadata = action.payload;
        state.skuOverwriteFields = overwriteFieldsMetadata;

        let overwriteFormObject = {};

        // generate an json object from array of object where key will be object.name and value will be object.values[0]
        overwriteFieldsMetadata.fields.forEach((element) => {
          // eslint-disable-next-line prefer-destructuring
          overwriteFormObject[element.name] = element.values[0];
        });

        state.createCurrentOverwrittenObj = overwriteFormObject;
        state.createCurrentOverwrittenObjBase = overwriteFormObject;
      })

      .addCase(createOverwriteSKU.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(createOverwriteSKU.rejected, (state) => {
        state.isSkuCRUDLoading = false;
      })
      .addCase(createOverwriteSKU.fulfilled, (state, action) => {
        // updating loading state
        state.isSkuCRUDLoading = false;

        // updating isSubmitDOne state
        state.isMoSubmitDone = true;
      })
      .addCase(deleteSku.pending, (state) => {
        state.isSkuCRUDLoading = true;
      })
      .addCase(deleteSku.rejected, (state, action) => {
        const { error } = action;
        if (
          error.message === InnovationErrorMessages.delete_sku_error_message
        ) {
          const { skuName } = action.meta.arg;

          let tableData = JSON.parse(
            JSON.stringify(state.innovationTableMetaData.row)
          );

          tableData = tableData.filter(
            (ele) => ele.parent.data.sku !== skuName
          );

          // checking for incomplete/identical rows post deleting sku
          const { isTableValid, tableArray } = detectIdenticalObjects(
            tableData,
            [...state.skuFields]
          );

          // updating the table state with incomplete/identical rows details
          state.innovationTableMetaData.row = tableArray;

          // updating the isTableValidState
          state.isTableValidState = isTableValid;
        }
        state.isSkuCRUDLoading = false;
      })
      .addCase(deleteSku.fulfilled, (state, action) => {
        // TODO: will delete the commented code post testing on dev
        // const skuData = action.payload;

        // const { skuName } = action.meta.arg;
        // Getting table data state
        // eslint-disable-next-line prefer-const
        // let tableData = JSON.parse(
        //   JSON.stringify(state.innovationTableMetaData.row)
        // );

        // // removing the deleted sku from table data by sku id
        // tableData = tableData.filter((ele) => ele.parent.data.sku !== skuName);

        // updating the simulation response for innovation grid on delete
        state.postSkuDeletionInnovationGridData = action.payload;

        // Delete triggers backend auto-simulation, so this response becomes
        // the new simulated baseline for unsaved-changes checks.
        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );

        // updating the table with simulated data after deleting the sku
        let innovationTableData = {
          ...action.payload.response
        };

        // checking for incomplete/identical rows post deleting sku
        const { isTableValid, tableArray } = detectIdenticalObjects(
          innovationTableData.row,
          [...state.skuFields]
        );

        state.innovationTableMetaData = {
          ...innovationTableData
        };

        // updating the table state with incomplete/identical rows details
        state.innovationTableMetaData.row = tableArray;

        // updating the isTableValidState
        state.isTableValidState = isTableValid;

        // Backend already returned simulated data for the delete flow.
        state.isSimulated = true;
 
        // setting innovates skus count
        if (action.payload.response.summary_strip.data.innovation_table) {
          state.skusInnovatedCount = action.payload.response.summary_strip.data
            .innovation_table.value
            ? action.payload.response.summary_strip.data.innovation_table.value
            : 0;
        }

        // updating loading state
        state.isSkuCRUDLoading = false;
      })
      .addCase(getInnovationTableData.pending, (state) => {
        state.fetchingTableData = true;
      })
      .addCase(getInnovationTableData.fulfilled, (state, action) => {
        state.fetchingTableData = false;



        let innovationTableData = {
          ...action.payload.response
        };

        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );

        // checking for incomplete/identical rows
        const { isTableValid, tableArray } = detectIdenticalObjects(
          innovationTableData.row,
          [...state.skuFields]
        );

        state.innovationTableMetaData = {
          ...innovationTableData
        };

        // updating the table state with incomplete/identical rows details
        state.innovationTableMetaData.row = tableArray;

        // updating the isTableValidState
        state.isTableValidState = isTableValid;

        // setting innovates skus count
        if (action.payload.response.summary_strip.data.innovation_table) {
          state.skusInnovatedCount = action.payload.response.summary_strip.data
            .innovation_table.value
            ? action.payload.response.summary_strip.data.innovation_table.value
            : 0;
        }

        // storing applied filters
        state.appliedFilters = action.meta.arg.filters;

        // updating the simulation state
        state.isSimulated = false;
      })
      .addCase(getInnovationTableData.rejected, (state) => {
        state.fetchingTableData = false;
        state.fetchingSimulatedData = false;
        // updating the simulation state
        state.isSimulated = false;
      })
      .addCase(getSimulatedInnovationData.pending, (state) => {
        state.fetchingSimulatedData = true;
      })
      .addCase(getSimulatedInnovationData.rejected, (state) => {
        state.fetchingSimulatedData = false;
        state.isMoSimulateDone = false;
      })
      .addCase(getSimulatedInnovationData.fulfilled, (state, action) => {
        state.fetchingSimulatedData = false;

        state.innovationTableMetaData = {
          ...action.payload.response
        };

        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );

        // updating the simulation state
        state.isSimulated = true;

        // updating isSubmitModDOne to false
        state.isMoSubmitDone = false;

        // updating the MO simulation flag to false
        state.isMoSimulateDone = false;
      })
      .addCase(getResetSimulatedInnovation.pending, (state) => {
        state.fetchingTableData = true;
      })
      .addCase(getResetSimulatedInnovation.rejected, (state) => {
        state.fetchingTableData = false;
      })
      .addCase(getResetSimulatedInnovation.fulfilled, (state, action) => {
        state.fetchingTableData = false;

        state.innovationTableMetaData = {
          ...action.payload.response
        };

        // updating the simulation state
        state.isSimulated = false;
      })
      .addCase(getSaveSimulatedInnovation.pending, (state) => {
        state.savingSimulation = true;
      })
      .addCase(getSaveSimulatedInnovation.rejected, (state) => {
        state.savingSimulation = false;
      })
      .addCase(getSaveSimulatedInnovation.fulfilled, (state, action) => {
        state.savingSimulation = false;
      })

      .addCase(getDuplicateSkuPopupFilters.pending, (state) => {
        state.fetchingDuplicateSkuPopupFilters = true;
      })
      .addCase(getDuplicateSkuPopupFilters.rejected, (state) => {
        state.fetchingDuplicateSkuPopupFilters = false;
      })
      .addCase(getDuplicateSkuPopupFilters.fulfilled, (state, action) => {
        state.fetchingDuplicateSkuPopupFilters = false;
        const filters: IMultiSelectControlField2[] = toFormFields2(
          action.payload.filter_metadata
            .slice()
            .sort(
              (filterA, filterB) =>
                filterA.placement_order - filterB.placement_order
            )
        );
        const { combinations } = action.payload;
        const filterToValuesMap = filters.reduce<{
          [key: string]: ISelectOption[];
        }>(
          (previousValue, currentValue) => ({
            ...previousValue,
            ...{ [currentValue.name]: currentValue.values }
          }),
          {}
        );
        state.topFiltersValue.initialValues = filterToValuesMap;
        state.topFiltersValue.combinations = combinations;
        // setting filters value
        const filtersData = filters.map((filter: IMultiSelectControlField2) => {
          const filterName = filter.name;
          if (filterToValuesMap[filterName]) {
            // eslint-disable-next-line no-param-reassign
            filter.values = filterToValuesMap[filterName].slice();
          }
          return filter;
        });
        state.topFiltersValue.filters = filtersData;

        // setting default values of duplicate sku dialog filters
        state.topFiltersDefaultValue.initialValues = filterToValuesMap;
        state.topFiltersDefaultValue.combinations = combinations;
        state.topFiltersDefaultValue.filters = filtersData;
      })
      .addCase(getDuplicateSkuTable.pending, (state) => {
        state.fetchingDuplicateSkuTable = true;
      })
      .addCase(getDuplicateSkuTable.rejected, (state) => {
        state.fetchingDuplicateSkuTable = false;
      })
      .addCase(getDuplicateSkuTable.fulfilled, (state, action) => {
        state.fetchingDuplicateSkuTable = false;

        // setting duplicateSkuTableData
        state.duplicateSkuTableData = {
          ...action.payload
        };
      })
});

export const {
  setInputFieldValues,
  editInputFieldValues,
  toggleTableDataFetched,
  updateFooter,
  overwriteInnovation,
  updateOverwriteInputFields,
  resetOverwriteInputFields,
  updateOvewrwrittenTableRow,
  updateOverwrittenSimulateFlag,
  closeOverwritePopup,
  closeSkuPop,
  setskuPopupType,
  resetInputFieldValues,
  updateFilterFieldOptions,
  setDuplicateSkuCount,
  resetDuplicateSku,
  resetDuplicateDialog,
  toggleDisableStateForSkuFormFields,
  setSkuFormFieldOptions
} = innovationSlice.actions;

export default innovationSlice;
